import { cookieStorage, makePersisted } from "@solid-primitives/storage";
import { useNavigate, useSearchParams } from "@solidjs/router";
import { type ViewId, ViewIdSchema } from "@storytell/types";
import lzString from "lz-string";
import { createEffect, createMemo, createSignal, onMount } from "solid-js";
import { isServer } from "solid-js/web";

export type KnowledgeUIService = ReturnType<typeof createKnowledgeUIService>;

// In charge of all the logic for creating knowledge management views, filtering,
// and navigation inside the knowledge pane
export const createKnowledgeUIService = () => {
  const nav = useUrlParamNavigation();

  return {
    ...nav,
  };
};
const constViews = ["all", "new"] as const;

type KnowledgeViewType = (typeof constViews)[number] | ViewId;

// We store the navigation state in the knowledge panel in a
// url search param so that users can use browser navigation to
// go back and forth, and for better support for SSR
const useUrlParamNavigation = () => {
  const mainParam = "kpp"; // knowledge panel path
  const navigate = useNavigate();

  const defaultTab = "assets" as const;
  const defaultView = "all" as const;

  const [lastPath, setLastPath] = createSignal<string[]>([defaultView, defaultTab]);

  const encodePath = (path: string[]) => lzString.compressToEncodedURIComponent(path.join("/"));
  const decodePath = (path: string): [KnowledgeViewType, "concepts" | "assets"] | undefined => {
    const decoded = lzString.decompressFromEncodedURIComponent(path).split("/");
    if (!Array.isArray(decoded) || decoded.length === 0) return undefined;
    if (!constViews.includes(decoded[0] as "all" | "new") && ViewIdSchema.safeParse(decoded[0]).success === false)
      return undefined;

    const view = decoded[0] as KnowledgeViewType;
    if (decoded.length === 1) return [view, defaultTab];
    if (!["concepts", "assets"].includes(decoded[1])) return [view, defaultTab];
    return decoded as [KnowledgeViewType, "concepts" | "assets"];
  };

  const [params] = useSearchParams();
  const path = () => {
    const encoded = params[mainParam];
    if (typeof encoded !== "string") return undefined;
    return decodePath(encoded);
  };

  if (!isServer) {
    createEffect(() => {
      if (path() !== undefined) {
        setLastPath(path()!);
      }
    });
  }

  const activeView = createMemo(() => path()?.[0]);
  const setActiveView = (view: KnowledgeViewType) => {
    const newPath = path() ?? [view, defaultTab];
    newPath[0] = view;

    const url = new URL(window.location.href);
    url.searchParams.set(mainParam, encodePath(newPath));
    const newUrl = url.pathname + url.search + url.hash;
    navigate(newUrl);
  };

  const activeTab = createMemo(() => path()?.[1]);
  const setActiveTab = (tab: "concepts" | "assets") => {
    const newPath = path() ?? [defaultView, tab];
    newPath[1] = tab;
    const url = new URL(window.location.href);
    url.searchParams.set(mainParam, encodePath(newPath));
    const newUrl = url.pathname + url.search + url.hash;
    navigate(newUrl);
  };

  const isOpen = () => path() !== undefined;

  const open = () => {
    const url = new URL(window.location.href);
    url.searchParams.set(mainParam, encodePath(lastPath()));
    const newUrl = url.pathname + url.search + url.hash;
    navigate(newUrl);
  };

  const close = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(mainParam);
    const newUrl = url.pathname + url.search + url.hash;
    navigate(newUrl);
  };

  return {
    activeView,
    activeTab,
    setActiveView,
    setActiveTab,
    isOpen,
    open,
    close,
  };
};
